// src/pages/CardGallery.js - Updated for 420 card system
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CardDisplay from '../components/CardDisplay';
import WorkoutProgressionModule from '../components/WorkoutProgressionModule';
import { STATIC_CARD_ORDER, getTotalCardCount } from '../data/StaticCardMapping';
import { getCardContent } from '../data/CardContent';
import { soundManager } from '../utils/SoundManager';
import './CardGallery.css';

// Create virtual cards from the static mapping
const createVirtualCard = (mappedCard, position) => {
    const content = getCardContent(position.toString().padStart(3, '0'));

    return {
        id: mappedCard.id,
        cardNumber: position,
        name: content ? content.characterName : `Card ${position.toString().padStart(3, '0')}`,
        type: mappedCard.folder === 'battle-cards' ? 'Battle' :
            mappedCard.folder === 'build-cards' ? 'Build' :
                mappedCard.folder === 'train-cards' ? 'Train' : 'Move',
        description: content ? content.story : `This is card ${position}`,
        image: `/assets/cards/${mappedCard.folder}/${mappedCard.image}`,
        fallbackImage: '/assets/cards/card-backs/cardbackdemoglowFIN.png', // Add fallback
        rarity: { name: 'Common', color: '#808080' },
        unlocked: true, // All cards viewable for now
        abilities: content ? content.abilities || content.specialties || ['Unknown'] : ['Unknown'],
        category: content ? (content.element || content.expertise || 'Unknown') : 'Unknown',
        attack: Math.floor(Math.random() * 50) + 50,
        defense: Math.floor(Math.random() * 50) + 50,
        health: Math.floor(Math.random() * 50) + 70,
        energy: Math.floor(Math.random() * 40) + 60
    };
};

const CardGallery = () => {
    const navigate = useNavigate();
    const [cards, setCards] = useState([]);
    const [filteredCards, setFilteredCards] = useState([]);
    const [selectedCard, setSelectedCard] = useState(null);
    const [filters, setFilters] = useState({
        category: 'all',
        rarity: 'all',
        type: 'all',
        owned: 'all'
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('type');
    const [viewMode, setViewMode] = useState('grid');

    useEffect(() => {
        // Create virtual cards from static mapping
        const virtualCards = STATIC_CARD_ORDER.map(mappedCard =>
            createVirtualCard(mappedCard, mappedCard.position)
        );

        setCards(virtualCards);
        setFilteredCards(virtualCards);
        soundManager.playSound('gameIntroMode2');
    }, []);

    useEffect(() => {
        filterAndSortCards();
    }, [filters, searchTerm, sortBy, cards]);

    const filterAndSortCards = () => {
        let filtered = [...cards];

        if (filters.category !== 'all') {
            filtered = filtered.filter(card =>
                card.category && card.category.toLowerCase() === filters.category
            );
        }

        if (filters.rarity !== 'all') {
            filtered = filtered.filter(card =>
                card.rarity && card.rarity.name.toLowerCase() === filters.rarity
            );
        }

        if (filters.type !== 'all') {
            filtered = filtered.filter(card =>
                card.type && card.type.toLowerCase() === filters.type
            );
        }

        if (filters.owned !== 'all') {
            filtered = filtered.filter(card =>
                filters.owned === 'owned' ? card.unlocked : !card.unlocked
            );
        }

        if (searchTerm) {
            filtered = filtered.filter(card =>
                card.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                card.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (card.abilities && card.abilities.some(ability =>
                    ability.toLowerCase().includes(searchTerm.toLowerCase())
                ))
            );
        }

        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    return a.name.localeCompare(b.name);
                case 'rarity':
                    const rarityOrder = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'mythic'];
                    return rarityOrder.indexOf(a.rarity.name.toLowerCase()) -
                        rarityOrder.indexOf(b.rarity.name.toLowerCase());
                case 'power':
                    const aPower = calculateCardPower(a);
                    const bPower = calculateCardPower(b);
                    return bPower - aPower;
                case 'attack':
                    return (b.attack || 0) - (a.attack || 0);
                case 'defense':
                    return (b.defense || 0) - (a.defense || 0);
                case 'type':
                    const typeCompare = a.type.localeCompare(b.type);
                    return typeCompare !== 0 ? typeCompare : a.name.localeCompare(b.name);
                case 'number':
                default:
                    return (a.cardNumber || 999999) - (b.cardNumber || 999999);
            }
        });

        setFilteredCards(filtered);
    };

    const calculateCardPower = (card) => {
        return ((card.attack || 0) + (card.defense || 0) + (card.health || 0) + (card.energy || 0)) / 4;
    };

    const handleCardClick = (card) => {
        setSelectedCard(card);
        soundManager.playSound('next');
    };

    const handleFilterChange = (filterType, value) => {
        setFilters(prev => ({
            ...prev,
            [filterType]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            category: 'all',
            rarity: 'all',
            type: 'all',
            owned: 'all'
        });
        setSearchTerm('');
        setSortBy('type');
    };

    const getStatsOverview = () => {
        const total = getTotalCardCount();
        const owned = cards.filter(card => card.unlocked).length;
        const percentage = total > 0 ? Math.round((owned / total) * 100) : 0;
        return { total, owned, percentage };
    };

    const getCardTypeBreakdown = () => {
        const breakdown = {
            Train: cards.filter(card => card.type === 'Train').length,
            Move: cards.filter(card => card.type === 'Move').length,
            Battle: cards.filter(card => card.type === 'Battle').length,
            Build: cards.filter(card => card.type === 'Build').length
        };
        return breakdown;
    };

    const handleCardPageView = (card) => {
        const pageNumber = card.cardNumber.toString().padStart(3, '0');
        navigate(`/battle-cards/${pageNumber}`);
    };

    const statsOverview = getStatsOverview();
    const typeBreakdown = getCardTypeBreakdown();

    return (
        <div className="card-gallery">
            {/* Header */}
            <div className="gallery-header">
                <button
                    className="back-button"
                    onClick={() => navigate('/')}
                >
                    ← Back to Home
                </button>
                <h1>Cards Collection</h1>
                <div className="collection-stats">
                    <span className="stat-item">
                        {statsOverview.owned}/{statsOverview.total} Cards
                    </span>
                    <span className="stat-item">
                        {statsOverview.percentage}% Complete
                    </span>
                </div>
            </div>

            {/* Card Type Breakdown */}
            <div className="type-breakdown">
                <div className="breakdown-item train">
                    <span className="type-name">Train</span>
                    <span className="type-count">{typeBreakdown.Train}</span>
                </div>
                <div className="breakdown-item move">
                    <span className="type-name">Move</span>
                    <span className="type-count">{typeBreakdown.Move}</span>
                </div>
                <div className="breakdown-item battle">
                    <span className="type-name">Battle</span>
                    <span className="type-count">{typeBreakdown.Battle}</span>
                </div>
                <div className="breakdown-item build">
                    <span className="type-name">Build</span>
                    <span className="type-count">{typeBreakdown.Build}</span>
                </div>
            </div>

            {/* Search and Filters */}
            <div className="gallery-controls">
                <div className="search-section">
                    <input
                        type="text"
                        placeholder="Search cards by name, description, or abilities..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>

                <div className="filters-section">
                    <select
                        value={filters.type}
                        onChange={(e) => handleFilterChange('type', e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Types</option>
                        <option value="battle">Battle</option>
                        <option value="build">Build</option>
                        <option value="train">Train</option>
                        <option value="move">Move</option>
                    </select>

                    <select
                        value={filters.owned}
                        onChange={(e) => handleFilterChange('owned', e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Cards</option>
                        <option value="owned">Available</option>
                        <option value="locked">Coming Soon</option>
                    </select>
                </div>

                <div className="sort-section">
                    <label>Sort by:</label>
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="sort-select"
                    >
                        <option value="number">Card Number</option>
                        <option value="type">Card Type</option>
                        <option value="name">Name</option>
                    </select>
                </div>

                <div className="view-controls">
                    <button
                        className={`view-button ${viewMode === 'grid' ? 'active' : ''}`}
                        onClick={() => setViewMode('grid')}
                    >
                        Grid
                    </button>
                    <button
                        className={`view-button ${viewMode === 'list' ? 'active' : ''}`}
                        onClick={() => setViewMode('list')}
                    >
                        List
                    </button>
                    <button
                        className="clear-filters-button"
                        onClick={clearFilters}
                    >
                        Clear Filters
                    </button>
                </div>
            </div>

            {/* Results Summary */}
            <div className="results-summary">
                <span>Showing {filteredCards.length} of {cards.length} cards</span>
                {searchTerm && <span>for "{searchTerm}"</span>}
            </div>

            {/* Cards Display */}
            <div className={`cards-container ${viewMode}`}>
                {filteredCards.length > 0 ? (
                    filteredCards.map(card => (
                        <div key={card.id} className="card-container-wrapper">
                            <CardDisplay
                                card={card}
                                size={viewMode === 'grid' ? 'medium' : 'small'}
                                showStats={true}
                                interactive={true}
                                onClick={handleCardClick}
                                isSelected={selectedCard?.id === card.id}
                                isOwned={card.unlocked}
                            />
                            <div className="card-actions-overlay">
                                <button
                                    className="card-page-button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleCardPageView(card);
                                    }}
                                    title={`View Card #${card.cardNumber.toString().padStart(3, '0')}`}
                                >
                                    #{card.cardNumber.toString().padStart(3, '0')}
                                </button>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="no-results">
                        <h3>No cards found</h3>
                        <p>Try adjusting your search or filters</p>
                        <button onClick={clearFilters} className="clear-filters-button">
                            Clear All Filters
                        </button>
                    </div>
                )}
            </div>

            {/* Card Detail Modal */}
            {selectedCard && (
                <div className="card-modal-overlay" onClick={() => setSelectedCard(null)}>
                    <div className="card-modal" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="modal-close"
                            onClick={() => setSelectedCard(null)}
                        >
                            ×
                        </button>
                        <CardDisplay
                            card={selectedCard}
                            size="large"
                            showStats={true}
                            interactive={false}
                            isOwned={selectedCard.unlocked}
                        />
                        <div className="card-actions">
                            <button
                                className="action-button primary"
                                onClick={() => handleCardPageView(selectedCard)}
                            >
                                View Full Card Page #{selectedCard.cardNumber.toString().padStart(3, '0')}
                            </button>
                            <button
                                className="action-button secondary"
                                onClick={() => setSelectedCard(null)}
                            >
                                Close
                            </button>
                        </div>

                        {/* Add the workout progression module */}
                        <WorkoutProgressionModule />
                    </div>
                </div>
            )}
        </div>
    );
};

export default CardGallery;